import { useState } from "react";
import {
  LucideChevronLeft,
  LucideChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StudentReportCard } from "@/components/entities/StudentReportCard";

/**
 * Renders a paginated list of student report card rows inside a class.
 * Loading and empty states are handled by the parent page.
 *
 * Props:
 *   reports           - array of report objects { _id, studentName, data: { oen } }
 *   reportGroupId     - class ID used to build edit links
 *   size              - "sm" | "md" (default "md") - passed to each card
 *   totalPages        - total pages for pagination
 *   currentPage       - current page index (0-based)
 *   onPageChange      - (newPage) => void
 *   onDelete          - (card) => void
 *   onToggleComplete  - optional (card) => void - called after internal toggle for side effects
 *   showCheck         - boolean - passed to each card (card default: true for md, false for sm)
 *   showOen           - boolean - passed to each card (card default: true)
 *   showName          - boolean - passed to each card (card default: true)
 *   showMenu          - boolean - passed to each card (card default: true for md, false for sm)
 *   onClick           - (card) => void - called on card link click
 */
export function StudentReportList({
  reports = [],
  reportGroupId,
  size,
  totalPages = 1,
  currentPage = 0,
  onPageChange,
  onDelete,
  onToggleComplete,
  showCheck,
  showOen,
  showName,
  showMenu,
  onClick,
}) {
  const [completedIds, setCompletedIds] = useState(new Set());

  function handleToggle(card) {
    setCompletedIds((prev) => {
      const next = new Set(prev);
      next.has(card._id) ? next.delete(card._id) : next.add(card._id);
      return next;
    });
    onToggleComplete?.(card);
  }

  return (
    <div className="space-y-2">
      {reports.map((card) => (
        <StudentReportCard
          key={card._id}
          card={card}
          reportGroupId={reportGroupId}
          size={size}
          isComplete={completedIds.has(card._id)}
          onToggleComplete={handleToggle}
          onDelete={onDelete}
          showCheck={showCheck}
          showOen={showOen}
          showName={showName}
          showMenu={showMenu}
          onClick={onClick ? () => onClick(card) : undefined}
        />
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage === 0}
            className="gap-1"
          >
            <LucideChevronLeft size={14} />
            Prev
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="gap-1"
          >
            Next
            <LucideChevronRight size={14} />
          </Button>
        </div>
      )}
    </div>
  );
}
